import { useState, forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, Send, CheckCircle2, Mail, Phone, MapPin, Globe, Linkedin, Instagram } from 'lucide-react';
import { useCompanySettings } from '@/hooks/use-company-settings';
import { motion } from "framer-motion";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Footer } from "@/components/footer";
import portalOneLogo from "@/assets/portalone-suite-logo.png";
import { LoginDropdown } from "@/components/LoginDropdown";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { ThemeToggle } from "@/components/theme-toggle";

const brand = {
  cyan: "#007CBE",
  neon: "#A3FF3F",
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const contactSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  company: z.string().optional(),
  doc_cnpj: z.string().optional(),
  phone: z.string().optional(),
  interest: z.string().min(1, 'Selecione um interesse'),
  message: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres'),
  honeypot: z.string().max(0, 'Spam detected'),
});

type ContactForm = z.infer<typeof contactSchema>;

function GlowHalo({ className = "" }: { className?: string }) {
  return (
    <div className={`pointer-events-none absolute inset-0 -z-10 blur-3xl ${className}`} aria-hidden>
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full" style={{ background: `radial-gradient(60% 60% at 50% 50%, ${brand.cyan}66 0%, transparent 65%)` }} />
      <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full" style={{ background: `radial-gradient(60% 60% at 50% 50%, ${brand.neon}66 0%, transparent 65%)` }} />
    </div>
  );
}

function NeonButton({ children, className = "", disabled, type = "button", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      disabled={disabled}
      className={`group relative overflow-hidden rounded-2xl px-5 py-3 font-semibold text-black shadow-md transition hover:shadow-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ background: disabled ? '#888' : `linear-gradient(90deg, ${brand.neon}, ${brand.cyan})`, boxShadow: disabled ? 'none' : `0 0 30px ${brand.neon}44, 0 0 50px ${brand.cyan}33` }}
      {...props}
    >
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      {!disabled && <span className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20" style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }} />}
    </button>
  );
}

const LoginButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ children, ...props }, ref) => (
    <button
      ref={ref}
      className="group relative overflow-hidden rounded-2xl px-5 py-3 font-semibold text-black shadow-md transition hover:shadow-lg focus:outline-none"
      style={{ background: `linear-gradient(90deg, ${brand.neon}, ${brand.cyan})`, boxShadow: `0 0 30px ${brand.neon}44, 0 0 50px ${brand.cyan}33` }}
      {...props}
    >
      <span className="relative z-10 inline-flex items-center gap-2">{children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5"/></span>
      <span className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20" style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }} />
    </button>
  )
);
LoginButton.displayName = "LoginButton";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { toast } = useToast();
  const { data: companySettings } = useCompanySettings();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      honeypot: '',
    },
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    
    try {
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'contact_submitted',
          source: 'contact',
          interest: data.interest,
        });
      }

      // Contact form submission - just show success message
      // No lead tracking - system discontinued
      console.log('Contact form submitted:', {
        name: data.name,
        email: data.email,
        interest: data.interest,
      });

      toast({
        title: 'Mensagem enviada!',
        description: 'Entraremos em contato em breve. Obrigado pelo interesse!',
      });

      reset();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar',
        description: 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 z-50 w-full backdrop-blur-sm bg-background/80 supports-[backdrop-filter]:bg-background/60 border-b border-border transition-colors duration-300">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src={portalOneLogo} 
              alt="PortalONE Suite" 
              className="h-16 md:h-20 w-auto object-contain" 
            />
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Home
            </Link>
              {[
                ["Sistemas", "/#sistemas"],
                ["Benefícios", "/#beneficios"],
                ["Planos", "/plans"],
                ["Contato", "/contact"],
              ].map(([label, href]) => (
              href === "/contact" ? (
                <Link 
                  key={label}
                  to={href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {label}
                </Link>
              ) : (
                <Link
                  key={label}
                  to={href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {label}
                </Link>
              )
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LoginDropdown 
              onForgotPassword={() => setShowForgotPassword(true)}
              triggerButton={<LoginButton>Login</LoginButton>}
            />
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div aria-hidden className="h-20 md:h-24" />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <GlowHalo />
        <div className="absolute inset-0 -z-10 opacity-20 dark:opacity-30" style={{
          background: `radial-gradient(120% 80% at 0% 0%, ${brand.cyan}55 0%, transparent 55%), radial-gradient(120% 80% at 100% 100%, ${brand.neon}55 0%, transparent 55%)`,
        }} />
        <div className="mx-auto max-w-7xl px-4 py-20">
          <motion.div variants={fadeIn} initial="hidden" animate="show" className="max-w-3xl">
            <h1 className="text-4xl font-bold leading-tight md:text-5xl text-foreground">
              Fale com o <span className="bg-gradient-to-r from-[#A3FF3F] to-[#007CBE] bg-clip-text text-transparent">PortalONE Suite</span>
            </h1>
            <p className="mt-3 text-muted-foreground">Tire dúvidas, solicite uma demonstração ou fale com nosso time comercial e técnico.</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative border-t border-border py-16 overflow-hidden">
        <GlowHalo />
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:grid-cols-2">
          {/* Form */}
          <motion.div variants={fadeIn} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-3xl border border-border bg-card p-6 shadow-xl">
            <h2 className="text-2xl font-semibold text-foreground">Envie sua mensagem</h2>
            <p className="mt-1 text-sm text-muted-foreground">Responderemos o mais rápido possível.</p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {/* Honeypot */}
              <input
                {...register('honeypot')}
                type="text"
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Nome*</span>
                  <input 
                    {...register('name')}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                    placeholder="Seu nome completo" 
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name.message}</p>}
                </label>
                <label className="block">
                  <span className="text-sm text-muted-foreground">E-mail*</span>
                  <input 
                    type="email" 
                    {...register('email')}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                    placeholder="voce@empresa.com" 
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Empresa</span>
                  <input 
                    {...register('company')}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                    placeholder="Nome da empresa" 
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-muted-foreground">CNPJ</span>
                  <input 
                    {...register('doc_cnpj')}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                    placeholder="00.000.000/0000-00" 
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Telefone</span>
                  <input 
                    {...register('phone')}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                    placeholder="(11) 99999-9999" 
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-muted-foreground">Interesse*</span>
                  <select 
                    {...register('interest')}
                    onChange={(e) => setValue('interest', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground"
                  >
                    <option value="">Selecione...</option>
                    <option value="demo">Agendar demonstração</option>
                    <option value="fleetone">FleetONE - Gestão de frotas</option>
                    <option value="pricing">Informações sobre preços</option>
                    <option value="partnership">Parceria</option>
                    <option value="support">Suporte</option>
                    <option value="other">Outro</option>
                  </select>
                  {errors.interest && <p className="mt-1 text-xs text-red-400">{errors.interest.message}</p>}
                </label>
              </div>

              <label className="block">
                <span className="text-sm text-muted-foreground">Mensagem*</span>
                <textarea 
                  {...register('message')}
                  className="mt-1 h-36 w-full resize-y rounded-xl border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" 
                  placeholder="Conte-nos mais sobre suas necessidades..." 
                />
                {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message.message}</p>}
              </label>

              <NeonButton className="w-full" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Enviando..." : <><Send className="h-4 w-4"/> Enviar mensagem</>}
              </NeonButton>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={fadeIn} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-6">
            {/* Canais diretos */}
            <div className="rounded-3xl border border-border bg-card p-6 shadow-md">
              <h3 className="text-xl font-semibold text-foreground">Canais diretos</h3>
              <p className="mt-1 text-muted-foreground">Prefere falar agora? Estamos por aqui.</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <a href={`mailto:${companySettings?.email || "contato@portalone.com.br"}`} className="group flex items-center gap-3 rounded-2xl border border-border bg-muted/50 p-4 transition hover:bg-muted">
                  <Mail className="h-5 w-5 text-primary"/> 
                  <span className="text-sm text-foreground">{companySettings?.email || "contato@portalone.com.br"}</span>
                </a>
                <a href={`tel:${companySettings?.telefone || "+55XXXXXXXXXX"}`} className="group flex items-center gap-3 rounded-2xl border border-border bg-muted/50 p-4 transition hover:bg-muted">
                  <Phone className="h-5 w-5 text-primary"/> 
                  <span className="text-sm text-foreground">{companySettings?.telefone || "(11) 3000-0000"}</span>
                </a>
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/50 p-4">
                  <MapPin className="h-5 w-5 text-primary"/> 
                  <span className="text-sm text-foreground">
                    {companySettings?.endereco_cidade ? 
                      `${companySettings.endereco_cidade} - ${companySettings.endereco_estado || 'SP'}` : 
                      'São Paulo - SP'
                    }
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/50 p-4">
                  <Globe className="h-5 w-5 text-primary"/> 
                  <span className="text-sm text-foreground">www.portalone.adm.br</span>
                </div>
              </div>
            </div>

            {/* Por que escolher */}
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="text-xl font-semibold text-foreground">Por que escolher o PortalONE?</h3>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "Integração completa entre todos os sistemas",
                  "Módulos sob demanda - pague apenas pelo que usa",
                  "Gestão multi-empresas em um só lugar",
                  "Suporte especializado e treinamento completo",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Redes */}
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="text-xl font-semibold text-foreground">Redes e atualizações</h3>
              <p className="mt-1 text-muted-foreground">Acompanhe novidades, status e releases.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href="#" className="inline-flex items-center gap-2 rounded-xl border border-border bg-muted/50 px-4 py-2 text-sm hover:bg-muted transition-colors">
                  <Linkedin className="h-4 w-4 text-primary"/> LinkedIn
                </a>
                <a href="#" className="inline-flex items-center gap-2 rounded-xl border border-border bg-muted/50 px-4 py-2 text-sm hover:bg-muted transition-colors">
                  <Instagram className="h-4 w-4 text-primary"/> Instagram
                </a>
                <a href="/status" className="inline-flex items-center gap-2 rounded-xl border border-border bg-muted/50 px-4 py-2 text-sm hover:bg-muted transition-colors">
                  <Globe className="h-4 w-4 text-primary"/> Status
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <WhatsAppFloat systemName="PortalONE Suite" systemCode="portal" />
      <ForgotPasswordModal open={showForgotPassword} onOpenChange={setShowForgotPassword} />
    </div>
  );
};

export default Contact;